import React, { useState, useEffect, useMemo } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Button,
  Checkbox,
  FormControlLabel,
  FormGroup,
  Box,
} from "@mui/material";
import {
  Tenant,
  TenantOperation,
  UpdateTenantRequest,
} from "../../../types/tenant";
import isValidAbsoluteUrl from "../../../services/validationHelper";

interface OwnProps {
  tenant?: Tenant;
  onClose: () => void;
  onSave: (tenantId: string, updateTenant: UpdateTenantRequest) => void;
}

const EditTenantDialog = ({ tenant, onClose, onSave }: OwnProps) => {
  const emptyUpdateTenantRequest = useMemo(
    () => ({
      hdmApiEndpoint: "",
      operations: [],
      hdmCredential: {
        username: "",
        password: "",
      },
    }),
    []
  );
  const [formData, setFormData] = useState<UpdateTenantRequest>(
    emptyUpdateTenantRequest
  );

  const [errors, setErrors] = useState({
    hdmApiEndpoint: false,
    username: false,
    password: false,
  });

  const handleInputChange = (
    e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>
  ) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
    setErrors((prev) => ({ ...prev, [name]: !value.trim() }));
  };

  const handleHdmCredentialChange = (
    e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>
  ) => {
    const { name, value } = e.target;
    setFormData((prev) => ({
      ...prev,
      hdmCredentials: {
        username: prev.hdmCredentials?.username || "",
        password: prev.hdmCredentials?.password || "",
        [name]: value,
      },
    }));
    setErrors((prev) => ({
      ...prev,
      [name]: !value.trim(),
    }));
  };

  const handleOperationToggle = (operation: TenantOperation) => {
    setFormData((prev) => {
      const isSelected = prev.operations.includes(operation);
      return {
        ...prev,
        operations: isSelected
          ? prev.operations.filter((op) => op !== operation)
          : [...prev.operations, operation],
      };
    });
  };

  const validateForm = (): boolean => {
    const usernameProvided = !!formData.hdmCredentials?.username.trim();
    const passwordProvided = !!formData.hdmCredentials?.password.trim();

    const newErrors = {
      hdmApiEndpoint: !isValidAbsoluteUrl(formData.hdmApiEndpoint.trim()),
      password: usernameProvided && !passwordProvided,
      username: passwordProvided && !usernameProvided,
    };

    setErrors(newErrors);

    return Object.values(newErrors).every((error) => !error);
  };

  useEffect(() => {
    if (tenant) {
      const data: UpdateTenantRequest = tenant
        ? {
            ...tenant,
            hdmCredentials: {
              username: "",
              password: "",
            },
          }
        : {
            hdmApiEndpoint: "",
            operations: [],
            hdmCredentials: {
              username: "",
              password: "",
            },
          };
      setFormData(data);
      setErrors({
        hdmApiEndpoint: false,
        username: false,
        password: false,
      });
    }
  }, [emptyUpdateTenantRequest, tenant]);

  const handleSave = () => {
    if (validateForm()) {
      onSave(tenant?.tenantId!, formData);
    }
  };

  return (
    <Dialog open={!!tenant} onClose={onClose} fullWidth maxWidth="sm">
      <DialogTitle>Edit Tenant</DialogTitle>
      <DialogContent>
        <TextField
          required
          label="HDM API Endpoint"
          name="hdmApiEndpoint"
          value={formData.hdmApiEndpoint}
          onChange={handleInputChange}
          fullWidth
          margin="normal"
          error={errors.hdmApiEndpoint}
          helperText={
            errors.hdmApiEndpoint ? "HDM API Endpoint is required" : ""
          }
        />
        <Box sx={{ display: "flex", gap: 2 }}>
          <TextField
            label="HDM Credential Username"
            name="username"
            value={formData.hdmCredentials?.username}
            onChange={handleHdmCredentialChange}
            fullWidth
            margin="normal"
            error={errors.username}
            helperText={
              errors.username
                ? "Username is required if password is provided"
                : ""
            }
          />
          <TextField
            label="HDM Credential Password"
            name="password"
            type="password"
            value={formData.hdmCredentials?.password}
            onChange={handleHdmCredentialChange}
            fullWidth
            margin="normal"
            error={errors.password}
            helperText={
              errors.password
                ? "Password is required if username is provided"
                : ""
            }
          />
        </Box>
        <div style={{ marginTop: "1rem" }}>
          <h4>Tenant Operations</h4>
          <FormGroup>
            {Object.values(TenantOperation).map((operation) => (
              <FormControlLabel
                key={operation}
                control={
                  <Checkbox
                    checked={formData.operations.includes(operation)}
                    onChange={() => handleOperationToggle(operation)}
                  />
                }
                label={operation}
              />
            ))}
          </FormGroup>
        </div>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Cancel</Button>
        <Button onClick={handleSave} variant="contained" color="primary">
          Save
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default EditTenantDialog;
